<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/training/question' }">题目管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-form
      ref="form"
      :model="form"
      :rules="formRules"
      size="medium"
      label-width="140px"
      label-position="left"
      @submit.native.prevent="onSubmit">
      <layout-panel>
        <template slot="header">表单</template>
        <el-alert
          v-if="form.status===consts.QUESTION_STATUS_UNUSED"
          class="question-lock-alert"
          type="info"
          :closable="false"
          show-icon
          title="当前题目为未使用，仅可修改题目标题、题目示例、答案选项、正确答案、题目解析和解析附件"/>
        <h3>分类信息</h3>
        <el-divider></el-divider>
        <el-form-item label="用户类型"
                      prop="target_usertype">
          <el-radio-group v-model="form.target_usertype"
                          :disabled="isBaseInfoDisabled">
            <el-radio v-for="item in opts.targetUserMaps"
                      :key="item.value"
                      :label="item.value">
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="是否通用"
                      prop="is_common">
          <el-radio-group v-model="form.is_common"
                          :disabled="isBaseInfoDisabled"
                          @change="onIsCommonChanged">
            <el-radio v-for="item in opts.isCommonMaps"
                      :key="item.value"
                      :label="item.value">
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="所属客户"
                      v-if="Number(form.is_common) === consts.IS_COMMON_DEDICATED"
                      prop="customerid">
          <remote-select
            v-model="form.customerid"
            class="width-max"
            clearable
            placeholder="请选择客户"
            :disabled="isBaseInfoDisabled"
            :query-request="getCustomerTable"
            :find-request="getCustomerDetail"
            @change="onCustomerChanged"/>
        </el-form-item>
        <el-form-item label="所属知识库"
                      prop="training_knowledge_id">
          <remote-select
            v-model="form.training_knowledge_id"
            class="width-max"
            clearable
            keyword-key="name"
            label-key="name"
            value-key="id"
            placeholder="请选择知识库"
            :disabled="knowledgeDisabled"
            :query-request="queryTrainingKnowledge"
            :find-request="getTrainingKnowledge"
            :query-params="knowledgeQueryParams"/>
        </el-form-item>

        <h3>基本信息</h3>
        <el-divider></el-divider>
        <el-form-item label="题型"
                      prop="question_type">
          <el-radio-group v-model="form.question_type"
                          :disabled="isBaseInfoDisabled"
                          @input="onQuestionTypeChanged">
            <el-radio v-for="item in opts.questionTypeMaps"
                      :key="item.value"
                      :label="item.value">
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <question-form
          :form="form"
          :question-type="form.question_type"
          :is-view-mode="isReadonly"
          :is-answer-locked="isAnswerLocked"
          :is-option-structure-locked="isAnswerLocked"
          @validate-field="validateField"
          @uploading-change="uploading = $event"/>
        <div class="form-actions">
          <el-button @click="$router.back()">取消</el-button>
          <el-button type="primary"
                     v-if="!isViewMode"
                     native-type="submit"
                     :loading="submitting || uploading">保存题目
          </el-button>
        </div>
      </layout-panel>
    </el-form>
  </div>
</template>

<script>
import trainingMixins from '@views/training/training.mixins'
import QuestionForm from '@views/training/components/QuestionForm.vue'
import {
  buildQuestionKnowledgeQueryParams,
  consts,
  createDefaultQuestionOptions,
  getQuestionSubmitPayload,
  isChoiceQuestion,
  isQuestionRelationEditLocked,
  isNumericAnswer,
  opts,
  resetQuestionAnswerByType, isQuestionUsedOrBaned,
} from '@views/training/training-question.constants'

function customerValidator (vm) {
  return (rule, value, callback) => {
    if (Number(vm.form.is_common) === consts.IS_COMMON_DEDICATED && !value) {
      callback(new Error('请选择所属客户'))
      return
    }
    callback()
  }
}

function answerValidator (vm) {
  return (rule, value, callback) => {
    if (vm.form.question_type === consts.QUESTION_TYPE_FILL_BLANK) {
      if (!isNumericAnswer(value)) {
        callback(new Error('填空题答案仅支持数字'))
        return
      }
      callback()
      return
    }
    if (vm.form.question_type === consts.QUESTION_TYPE_MULTI && (!Array.isArray(value) || value.length < 1)) {
      callback(new Error('请选择正确答案'))
      return
    }
    if (vm.form.question_type === consts.QUESTION_TYPE_SINGLE && !value) {
      callback(new Error('请选择正确答案'))
      return
    }
    if (!vm.form.options.every(item => item.title)) {
      callback(new Error('请填写完整选项内容'))
      return
    }
    callback()
  }
}

export default {
  components: {
    QuestionForm,
  },
  mixins: [trainingMixins],
  data () {
    return {
      id: 0,
      consts,
      opts,
      submitting: false,
      uploading: false,
      form: {
        question_type: consts.QUESTION_TYPE_SINGLE,
        title: '',
        sample_files: [],
        options: createDefaultQuestionOptions(),
        standard_answer: '',
        analysis_text: '',
        analysis_files: [],
        target_usertype: consts.TARGETUSER_TYPE_USER,
        is_common: consts.IS_COMMON_DEDICATED,
        customerid: null,
        training_knowledge_id: null,
        status: consts.QUESTION_STATUS_NEW,
      },
    }
  },
  computed: {
    // 查看
    isViewMode () {
      return (this.$route.query && this.$route.query.mode === 'view')
    },

    // 已使用/未使用/已作废
    isRelationEditLocked () {
      return isQuestionRelationEditLocked(this.id, this.form.status)
    },

    // 查看/已使用/未使用/已作废
    isBaseInfoDisabled () {
      return this.isViewMode || this.isRelationEditLocked
    },

    // 查看/已使用/已作废
    isReadonly () {
      return this.isViewMode || isQuestionUsedOrBaned(this.id, this.form.status)
    },

    // 查看/已使用/已作废
    isAnswerLocked () {
      return this.isViewMode || isQuestionUsedOrBaned(this.id, this.form.status)
    },

    formRules () {
      return {
        target_usertype: [{required: true, message: '请选择用户类型', trigger: 'change'}],
        is_common: [{required: true, message: '请选择是否通用', trigger: 'change'}],
        customerid: [{validator: customerValidator(this), trigger: 'change'}],
        training_knowledge_id: [{required: true, message: '请选择所属知识库', trigger: 'change'}],
        question_type: [{required: true, message: '请选择题型', trigger: 'change'}],
        title: [{required: true, message: '请输入题目标题', trigger: 'blur'}],
        standard_answer: [{validator: answerValidator(this), trigger: 'submit'}],
      }
    },
    knowledgeQueryParams () {
      return buildQuestionKnowledgeQueryParams(this.form)
    },
    knowledgeDisabled () {
      return this.isBaseInfoDisabled ||
        (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && !this.form.customerid)
    }
  },
  created () {
    if (this.$route.params.id) {
      this.id = Number(this.$route.params.id)
      this.loadDetail(this.id)
    }
  },
  methods: {
    ...request.map([
      'getCustomerTable',
      'getCustomerDetail',
      'queryTrainingKnowledge',
      'getTrainingKnowledge',
      'getTrainingQuestion',
      'createTrainingQuestion',
      'updateTrainingQuestion',
    ]),
    loadDetail (id, options = {}) {
      request.common('getTrainingQuestion', {id}).then(({data}) => {
        const questionJson = data.question_json || {}
        const analysis = questionJson.question_analysis || data.question_analysis || {}

        const standardAnswer = questionJson.standard_answer !== undefined && questionJson.standard_answer !== null
          ? questionJson.standard_answer : data.standard_answer

        const questionType = Number(data.question_type || questionJson.question_type || data.type) || consts.QUESTION_TYPE_SINGLE

        // 重新组装一下数据
        this.form = Object.assign({}, this.form, {
          target_usertype: Number(data.target_usertype) || consts.TARGETUSER_TYPE_USER,
          is_common: Number(data.is_common),
          customerid: Number(data.customerid) > 0 ? String(data.customerid) : null,
          training_knowledge_id: Number(data.training_knowledge_id) || null,
          question_type: questionType,
          title: data.title || questionJson.title || '',
          sample_files: Array.isArray(questionJson.sample_files) ? questionJson.sample_files : [],
          options: Array.isArray(questionJson.options) && questionJson.options.length ? questionJson.options : createDefaultQuestionOptions(),
          standard_answer: standardAnswer !== undefined && standardAnswer !== null
            ? standardAnswer
            : resetQuestionAnswerByType(questionType),
          analysis_text: analysis.analysis_text || '',
          analysis_files: Array.isArray(analysis.analysis_files) ? analysis.analysis_files : [],
          status: options.copy ? consts.QUESTION_STATUS_NEW : Number(data.status) || consts.QUESTION_STATUS_NEW,
        })
      })
    },
    onIsCommonChanged (value) {
      if (this.isBaseInfoDisabled) {
        return
      }
      if (Number(value) === consts.IS_COMMON_SHARED) {
        this.form.customerid = null
      }
      this.form.training_knowledge_id = null
      this.clearRelationValidate()
    },
    onCustomerChanged () {
      if (this.isBaseInfoDisabled) {
        return
      }
      this.form.training_knowledge_id = null
    },
    onQuestionTypeChanged (type) {
      if (this.isBaseInfoDisabled) {
        return
      }
      this.form.standard_answer = resetQuestionAnswerByType(type)
      if (isChoiceQuestion(type) && !this.form.options.length) {
        this.form.options = createDefaultQuestionOptions()
      }
      // this.validateField('standard_answer')
    },
    validateField (field) {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.validateField(field)
      })
    },
    clearRelationValidate () {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate(['customerid', 'training_knowledge_id'])
      })
    },
    onSubmit () {
      if (this.isViewMode) {
        return
      }
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.$message.error('请检查表单填写是否正确')
          return
        }
        const requestName = this.id ? 'updateTrainingQuestion' : 'createTrainingQuestion'
        const params = this.id
          ? {id: this.id, ...getQuestionSubmitPayload(this.form)}
          : getQuestionSubmitPayload(this.form)

        this.submitting = true
        request.common(requestName, params).then(() => {
          this.$message.success(this.id ? '保存成功' : '创建成功')
          this.$router.push({path: '/training/question'})
        }).finally(() => {
          this.submitting = false
        })
      })
    },
  },
}
</script>

<style scoped
       lang="scss">
.question-lock-alert {
  margin-bottom: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/question/TrainingQuestionForm.vue